import { ErrorModal } from "@dexteel/mesf-core";
import {
	Add,
	Close,
	ExpandLess,
	ExpandMore,
	Restore,
} from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Collapse,
	FormControlLabel,
	IconButton,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type InputHTMLAttributes,
	type KeyboardEvent,
	useEffect,
	useRef,
	useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useCalendarAreaId } from "../../context/CalendarAreaContext";
import {
	getEmailAddresses,
	getEmailRecipientsResources,
} from "../../repositories/CalendarRepository";
import type { CalendarEmailFormValues } from "./CalendarEmailForm";

const isValidEmail = (email: string): boolean =>
	!email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const createCustomEmail = () => ({
	id: `custom-${Date.now()}`,
	email: "",
	selected: true,
});

const hasInvalidSelectedCustomEmail = (
	customEmails: CalendarEmailFormValues["customEmails"],
): boolean =>
	customEmails.some(
		({ email, selected }) =>
			selected && Boolean(email.trim()) && !isValidEmail(email),
	);

const hasSelectedCustomEmail = (
	customEmails: CalendarEmailFormValues["customEmails"],
): boolean =>
	customEmails.some(({ email, selected }) => selected && Boolean(email.trim()));

const shouldNotifyByEmail = (
	required: boolean,
	selectedDefaultEmails: string[],
	customEmails: CalendarEmailFormValues["customEmails"],
): boolean =>
	required ||
	selectedDefaultEmails.length > 0 ||
	hasSelectedCustomEmail(customEmails);

interface DefaultRecipientRowsProps {
	emails: string[];
	selectedEmails: string[];
	hiddenEmails: Set<string>;
	onSelectionChange: (selectedEmails: string[]) => void;
	onHideEmail: (email: string) => void;
}

const DefaultRecipientRows = ({
	emails,
	selectedEmails,
	hiddenEmails,
	onSelectionChange,
	onHideEmail,
}: DefaultRecipientRowsProps) =>
	emails
		.filter((email) => !hiddenEmails.has(email))
		.map((email) => (
			<Box
				key={email}
				sx={{
					display: "flex",
					alignItems: "center",
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 1,
					mb: 1,
					px: 1,
					userSelect: "none",
				}}
			>
				<Checkbox
					checked={selectedEmails.includes(email)}
					onChange={(_, selected) =>
						onSelectionChange(
							selected
								? [...selectedEmails, email]
								: selectedEmails.filter((candidate) => candidate !== email),
						)
					}
					color="success"
					inputProps={{
						"aria-label": `Include default recipient ${email}`,
					}}
				/>
				<Typography variant="body2" sx={{ flex: 1 }}>
					{email}
				</Typography>
				<Typography variant="caption" color="text.disabled" fontSize={11}>
					Default
				</Typography>
				<IconButton
					size="small"
					onClick={() => onHideEmail(email)}
					sx={{ color: "text.secondary" }}
					aria-label={`Remove ${email}`}
				>
					<Close fontSize="small" />
				</IconButton>
			</Box>
		));

interface CustomRecipientRowsProps {
	customEmails: CalendarEmailFormValues["customEmails"];
	knownEmails: string[];
	required: boolean;
	onRemove: (index: number) => void;
}

const CustomRecipientRows = ({
	customEmails,
	knownEmails,
	required,
	onRemove,
}: CustomRecipientRowsProps) => {
	const { getValues, setValue } = useFormContext<CalendarEmailFormValues>();

	const updateCustomEmails = (
		nextCustomEmails: CalendarEmailFormValues["customEmails"],
	) => {
		setValue("customEmails", nextCustomEmails, {
			shouldDirty: true,
			shouldValidate: true,
		});
		setValue(
			"notifyByEmail",
			shouldNotifyByEmail(
				required,
				getValues("selectedDefaultEmails"),
				nextCustomEmails,
			),
			{ shouldDirty: true, shouldValidate: true },
		);
	};

	return customEmails.map((customEmail, index) => {
		const hasError = !isValidEmail(customEmail.email);
		const emailsTakenByOtherRows = customEmails
			.filter((_, customIndex) => customIndex !== index)
			.map(({ email }) => email.trim().toLowerCase());
		const options = knownEmails.filter(
			(email) => !emailsTakenByOtherRows.includes(email.toLowerCase()),
		);
		return (
			<Box
				key={customEmail.id}
				sx={{
					display: "flex",
					alignItems: "center",
					border: "1px solid",
					borderColor: hasError ? "error.main" : "divider",
					borderRadius: 1,
					mb: 1,
					px: 1,
					userSelect: "none",
				}}
			>
				<Checkbox
					checked={customEmail.selected && !hasError}
					onChange={(_, selected) =>
						updateCustomEmails(
							customEmails.map((value, customIndex) =>
								customIndex === index ? { ...value, selected } : value,
							),
						)
					}
					disabled={hasError}
					color="success"
					inputProps={{ "aria-label": "Include custom recipient" }}
				/>
				<Autocomplete
					freeSolo
					openOnFocus
					autoHighlight
					disableClearable
					options={options}
					value={customEmail.email}
					inputValue={customEmail.email}
					onInputChange={(_, nextEmail) =>
						updateCustomEmails(
							customEmails.map((value, customIndex) =>
								customIndex === index ? { ...value, email: nextEmail } : value,
							),
						)
					}
					sx={{ flex: 1 }}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="standard"
							placeholder="Type or select an email"
							slotProps={{
								input: { ...params.InputProps, disableUnderline: true },
								htmlInput: {
									...params.inputProps,
									"aria-label": "Custom email address",
									name: `custom-email-${index}`,
									autoComplete: "new-password",
								},
							}}
							sx={{
								"& .MuiInputBase-root": {
									fontSize: "0.875rem",
									color: hasError ? "error.main" : "inherit",
								},
								"& input::placeholder": {
									color: "text.disabled",
									opacity: 1,
								},
							}}
						/>
					)}
				/>
				{hasError && (
					<Typography variant="caption" color="error" sx={{ mr: 1 }}>
						Invalid
					</Typography>
				)}
				<IconButton
					size="small"
					onClick={() => onRemove(index)}
					sx={{ color: "text.secondary" }}
					aria-label="Remove custom email"
				>
					<Close fontSize="small" />
				</IconButton>
			</Box>
		);
	});
};

interface RecipientFieldsProps {
	emails: string[];
	knownEmails: string[];
	selectedDefaultEmails: string[];
	hiddenEmails: Set<string>;
	required: boolean;
	isLoadingEmails: boolean;
	customEmails: CalendarEmailFormValues["customEmails"];
	onDefaultSelectionChange: (selectedEmails: string[]) => void;
	onHideEmail: (email: string) => void;
	onAddEmail: () => void;
	onRemoveCustomEmail: (index: number) => void;
	onResetDefaults: () => void;
}

const RecipientFields = ({
	emails,
	knownEmails,
	selectedDefaultEmails,
	hiddenEmails,
	required,
	isLoadingEmails,
	customEmails,
	onDefaultSelectionChange,
	onHideEmail,
	onAddEmail,
	onRemoveCustomEmail,
	onResetDefaults,
}: RecipientFieldsProps) => {
	const { setValue, trigger } = useFormContext<CalendarEmailFormValues>();

	if (isLoadingEmails) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	return (
		<>
			<DefaultRecipientRows
				emails={emails}
				selectedEmails={selectedDefaultEmails}
				hiddenEmails={hiddenEmails}
				onSelectionChange={onDefaultSelectionChange}
				onHideEmail={onHideEmail}
			/>
			<CustomRecipientRows
				customEmails={customEmails}
				knownEmails={knownEmails.filter(
					(email) => !emails.includes(email) || hiddenEmails.has(email),
				)}
				required={required}
				onRemove={(index) => {
					onRemoveCustomEmail(index);
				}}
			/>
			<Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
				<Button startIcon={<Add />} onClick={onAddEmail} size="small">
					Add email
				</Button>
				{emails.length > 0 && hiddenEmails.size > 0 && (
					<Button
						startIcon={<Restore />}
						onClick={() => {
							onResetDefaults();
							setValue("selectedDefaultEmails", emails, {
								shouldDirty: true,
								shouldValidate: true,
							});
							void trigger("customEmails");
						}}
						size="small"
					>
						Reset to defaults
					</Button>
				)}
			</Box>
		</>
	);
};

const useEmailRecipients = ({
	resource,
	enabled = true,
}: {
	resource: string;
	enabled?: boolean;
}) => {
	const defaultAreaId = useCalendarAreaId();

	return useQuery<string[], Error>({
		queryKey: ["emailRecipients", resource, defaultAreaId],
		queryFn: ({ signal }) =>
			getEmailRecipientsResources({ resource, defaultAreaId }, signal),
		enabled,
	});
};
const useEmailAddresses = ({ enabled = true }: { enabled?: boolean }) => {
	return useQuery<string[], Error>({
		queryKey: ["emailAddresses"],
		queryFn: ({ signal }) => getEmailAddresses(signal),
		enabled,
	});
};

interface EmailNotificationFieldsProps {
	resource: string;
	open: boolean;
	loading?: boolean;
	required?: boolean;
	getDefaultValues: () => CalendarEmailFormValues;
	onReadyChange: (ready: boolean) => void;
}

export const EmailNotificationFields = ({
	resource,
	open,
	loading = false,
	required = false,
	getDefaultValues,
	onReadyChange,
}: EmailNotificationFieldsProps) => {
	const defaultAreaId = useCalendarAreaId();
	const queryClient = useQueryClient();

	const seedStateRef = useRef<"none" | "failed" | "success">("none");

	const [hiddenEmails, setHiddenEmails] = useState<Set<string>>(new Set());
	const [expanded, setExpanded] = useState(true);

	const { control, getValues, reset, setValue, trigger, watch } =
		useFormContext<CalendarEmailFormValues>();

	const notifyByEmail = watch("notifyByEmail");
	const selectedDefaultEmails = watch("selectedDefaultEmails");

	const {
		data: emails = [],
		isLoading: isLoadingRecipients,
		isSuccess,
		error,
		isError,
	} = useEmailRecipients({ resource, enabled: open });

	const { data: existingEmails = [] } = useEmailAddresses({ enabled: open });

	const ready = open && !isLoadingRecipients && (isSuccess || isError);

	const handleNotifyToggle = (nextValue: boolean) => {
		if (nextValue) {
			setValue(
				"selectedDefaultEmails",
				emails.filter((email) => !hiddenEmails.has(email)),
				{
					shouldDirty: true,
					shouldValidate: true,
				},
			);
			setExpanded(true);
			const currentCustomEmails = getValues("customEmails");
			setValue(
				"customEmails",
				emails.length === 0 && currentCustomEmails.length === 0
					? [createCustomEmail()]
					: currentCustomEmails.map((customEmail) => ({
							...customEmail,
							selected: true,
						})),
				{ shouldDirty: true, shouldValidate: true },
			);
		} else {
			setValue("selectedDefaultEmails", [], {
				shouldDirty: true,
				shouldValidate: true,
			});
			setValue(
				"customEmails",
				getValues("customEmails").map((customEmail) => ({
					...customEmail,
					selected: false,
				})),
				{ shouldDirty: true, shouldValidate: true },
			);
		}
	};

	const handleExpandKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			setExpanded(!expanded);
		}
	};

	useEffect(() => {
		reset(getDefaultValues());
		setHiddenEmails(new Set());
		setExpanded(true);
		seedStateRef.current = "none";
		onReadyChange(false);
	}, [getDefaultValues, onReadyChange, open, reset]);

	useEffect(() => {
		if (!ready) onReadyChange(false);
	}, [ready]);

	useEffect(() => {
		if (!ready) return;
		if (seedStateRef.current === "success") return;
		if (seedStateRef.current === "failed" && !isSuccess) return;
		seedStateRef.current = isSuccess ? "success" : "failed";

		setValue("selectedDefaultEmails", isSuccess ? emails : [], {
			shouldValidate: true,
		});
		setValue("publisherNote", "");

		const seedSucceeded = isSuccess;

		if (seedSucceeded && emails.length > 0) {
			setValue("notifyByEmail", true);
			setValue("customEmails", []);
		} else if (required) {
			setValue("notifyByEmail", true);
			setValue("customEmails", [createCustomEmail()]);
		} else {
			setValue("notifyByEmail", false);
			setValue("customEmails", []);
		}
		void trigger("customEmails");
		onReadyChange(true);
	}, [emails, isSuccess, ready, required]);

	return (
		<>
			<Box
				sx={{
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 1,
					p: 1,
					pl: 3,
					mt: 2,
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						cursor: notifyByEmail ? "pointer" : "default",
					}}
					onClick={notifyByEmail ? () => setExpanded(!expanded) : undefined}
					onKeyDown={notifyByEmail ? handleExpandKeyDown : undefined}
					role={notifyByEmail ? "button" : undefined}
					tabIndex={notifyByEmail ? 0 : undefined}
					aria-expanded={notifyByEmail ? expanded : undefined}
				>
					{required ? (
						<Typography variant="body1" fontWeight={500}>
							Recipients
						</Typography>
					) : (
						<FormControlLabel
							control={
								<Controller
									control={control}
									name="notifyByEmail"
									render={({ field }) => (
										<Switch
											checked={field.value}
											onChange={(event) => {
												event.stopPropagation();
												const nextValue = !field.value;
												field.onChange(nextValue);
												handleNotifyToggle(nextValue);
											}}
											onBlur={field.onBlur}
											inputRef={field.ref}
											onClick={(event) => event.stopPropagation()}
											color="success"
											slotProps={{
												input: {
													"data-test-id": "notify-by-email-switch",
												} as InputHTMLAttributes<HTMLInputElement>,
											}}
										/>
									)}
								/>
							}
							label={
								<Typography variant="body1" fontWeight={500}>
									Notify users by Email
								</Typography>
							}
							onClick={(event) => event.stopPropagation()}
						/>
					)}
					<Box sx={{ flexGrow: 1 }} />
					{expanded ? (
						<ExpandLess
							sx={{ color: notifyByEmail ? "inherit" : "action.disabled" }}
						/>
					) : (
						<ExpandMore
							sx={{ color: notifyByEmail ? "inherit" : "action.disabled" }}
						/>
					)}
				</Box>

				<Collapse in={expanded && notifyByEmail}>
					<Box sx={{ pl: 2, pt: 1 }}>
						<Controller
							control={control}
							name="customEmails"
							rules={{
								validate: (customEmails) =>
									(!hasInvalidSelectedCustomEmail(customEmails) &&
										(!required ||
											shouldNotifyByEmail(
												false,
												getValues("selectedDefaultEmails"),
												customEmails,
											))) ||
									"Enter a valid email and select at least one recipient.",
							}}
							render={({ field }) => (
								<RecipientFields
									emails={emails}
									knownEmails={existingEmails}
									selectedDefaultEmails={selectedDefaultEmails}
									hiddenEmails={hiddenEmails}
									required={required}
									isLoadingEmails={isLoadingRecipients}
									customEmails={field.value}
									onDefaultSelectionChange={(selectedEmails) => {
										setValue("selectedDefaultEmails", selectedEmails, {
											shouldDirty: true,
											shouldValidate: true,
										});
										setValue(
											"notifyByEmail",
											shouldNotifyByEmail(
												required,
												selectedEmails,
												getValues("customEmails"),
											),
											{ shouldDirty: true, shouldValidate: true },
										);
										void trigger("customEmails");
									}}
									onAddEmail={() =>
										setValue(
											"customEmails",
											[...field.value, createCustomEmail()],
											{
												shouldDirty: true,
												shouldValidate: true,
											},
										)
									}
									onHideEmail={(email) => {
										setHiddenEmails((current) => new Set(current).add(email));
										const nextSelected = getValues(
											"selectedDefaultEmails",
										).filter((candidate) => candidate !== email);
										setValue("selectedDefaultEmails", nextSelected, {
											shouldDirty: true,
											shouldValidate: true,
										});
										setValue(
											"notifyByEmail",
											shouldNotifyByEmail(
												required,
												nextSelected,
												getValues("customEmails"),
											),
											{ shouldDirty: true, shouldValidate: true },
										);
										void trigger("customEmails");
									}}
									onRemoveCustomEmail={(index) => {
										const nextCustomEmails = getValues("customEmails").filter(
											(_, customIndex) => customIndex !== index,
										);
										setValue("customEmails", nextCustomEmails, {
											shouldDirty: true,
											shouldValidate: true,
										});
										setValue(
											"notifyByEmail",
											shouldNotifyByEmail(
												required,
												getValues("selectedDefaultEmails"),
												nextCustomEmails,
											),
											{ shouldDirty: true, shouldValidate: true },
										);
									}}
									onResetDefaults={() => setHiddenEmails(new Set())}
								/>
							)}
						/>
					</Box>
				</Collapse>
			</Box>

			<Box sx={{ mt: 2 }}>
				<Controller
					control={control}
					name="publisherNote"
					render={({ field }) => (
						<TextField
							{...field}
							label="Note"
							placeholder="Add optional context to include in the notification email."
							multiline
							minRows={3}
							maxRows={6}
							fullWidth
							size="small"
							disabled={!notifyByEmail || loading}
							helperText={
								notifyByEmail
									? "Included in the body of the notification email."
									: "Enable email notification to add a note."
							}
							slotProps={{
								htmlInput: { "data-test-id": "publisher-note-input" },
							}}
						/>
					)}
				/>
			</Box>
			<ErrorModal
				error={
					isError
						? error instanceof Error
							? error.message
							: "Error fetching email recipients"
						: ""
				}
				onHide={() =>
					queryClient.resetQueries({
						queryKey: ["emailRecipients", resource, defaultAreaId],
					})
				}
			/>
		</>
	);
};
